/**
 * Appearance live preview — a full-tab look at one saved appearance (spec 50).
 *
 * The sibling of `template-preview-page.tsx` for the merchant's *own* designs:
 * opened in a new tab from the editor toolbar or the library cards, it renders
 * the appearance's **last saved version** through `ComposerPreview` — the same
 * components the storefront serves, dressed with the shared sample catalogue
 * where the design has no real data yet.
 *
 * Deliberately read-only and deliberately the *saved* state: the editor's own
 * canvas is the place for unsaved work, and a second surface that also showed
 * it would leave nothing trustworthy to compare against.
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, Monitor, Pencil, Smartphone, Tablet } from "lucide-react";
import { Alert, Skeleton } from "@flint/ui";
import { parseSections, parseTheme } from "@flint/api-contract/store-home";
import { trpc } from "../../../lib/trpc.ts";
import { WorkspaceShell } from "../../workspace/index.ts";
import { storeErrorMessage } from "../error-message.ts";
import { ComposerPreview, type PreviewDevice } from "./composer-preview.tsx";
import { formatVersionStamp } from "./version-picker.tsx";

const DEVICES: Array<{ id: PreviewDevice; label: string; Icon: typeof Monitor }> = [
  { id: "desktop", label: "Desktop", Icon: Monitor },
  { id: "tablet", label: "Tablet", Icon: Tablet },
  { id: "mobile", label: "Mobile", Icon: Smartphone },
];

export function AppearancePreviewPage({
  appearanceId,
  versionId,
}: {
  appearanceId: string;
  /**
   * Pin the preview to one saved version (`?version=` from the editor's
   * Preview link while the history picker has a version on the canvas).
   * Absent → the appearance's current version, via `storeHome.get`.
   */
  versionId?: string;
}) {
  const [device, setDevice] = useState<PreviewDevice>("desktop");
  // Always needed: store identity (name, slug, collections, phone, profile)
  // comes from here even when a specific version is pinned.
  const query = trpc.storeHome.get.useQuery({ appearanceId });

  // The pinned version's composition. The version-picker's own data path —
  // anything pickable there (the list is capped at 20) is resolvable here.
  const versions = trpc.storeHome.versions.useQuery(
    { appearanceId, withContent: true },
    { enabled: versionId !== undefined },
  );
  const pinned = versionId
    ? (versions.data?.items ?? []).find((v) => v.id === versionId)
    : undefined;

  if (query.error) {
    return (
      <WorkspaceShell breadcrumbs={[{ label: "Appearance preview" }]} hideSectionTabs>
        <div className="mx-auto max-w-lg py-16">
          <Alert intent="bad" title="Couldn't open that appearance">
            {storeErrorMessage(query.error)}
          </Alert>
          <Link
            to="/store/admin/storefront"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
          >
            <ArrowLeft size={14} aria-hidden />
            Back to Appearance
          </Link>
        </div>
      </WorkspaceShell>
    );
  }

  return (
    <WorkspaceShell
      breadcrumbs={[{ label: "Appearance preview" }]}
      hideTopbar
      hideSectionTabs
      disableViewportScroll
      contentClassName="mx-0 flex h-full w-full max-w-none"
    >
      <div className="flex h-full min-h-0 w-full flex-col">
        {/* ── Toolbar — same dark chrome as the editor ─────────── */}
        <div className="flex h-14 shrink-0 flex-wrap items-center gap-3 bg-[#1b2130] px-4 text-slate-100">
          <Link
            to="/store/admin/storefront"
            aria-label="Back to appearances"
            className="-ml-1 rounded-sm p-1 text-slate-300 transition hover:bg-white/10 hover:text-white"
          >
            <ArrowLeft size={18} aria-hidden />
          </Link>
          <div className="min-w-0">
            <span className="block truncate text-sm font-semibold text-white">
              {query.data?.appearanceName ?? "Appearance"}
            </span>
            <span className="hidden truncate text-xs text-slate-400 sm:block">
              {pinned
                ? `Preview of your saved version from ${formatVersionStamp(pinned.createdAt)}`
                : "Preview of your last saved version"}
            </span>
          </div>

          <div
            className="mx-auto flex gap-1 rounded-lg bg-black/25 p-1"
            role="group"
            aria-label="Preview device"
          >
            {DEVICES.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setDevice(id)}
                aria-pressed={device === id}
                title={label}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition ${
                  device === id ? "bg-white/15 text-white" : "text-slate-400 hover:text-slate-100"
                }`}
              >
                <Icon size={14} aria-hidden />
                <span className="hidden md:inline">{label}</span>
              </button>
            ))}
          </div>

          <Link
            to="/store/admin/storefront/$appearanceId"
            params={{ appearanceId }}
            className="flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-1.5 text-sm font-medium text-slate-100 transition hover:bg-white/10"
          >
            <Pencil size={13} aria-hidden />
            Edit
          </Link>
        </div>

        {/* ── Canvas ───────────────────────────────────────────── */}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[#eceef2] p-2.5">
          {!query.data || (versionId !== undefined && versions.isLoading) ? (
            <Skeleton className="h-full w-full" />
          ) : (
            <ComposerPreview
              // The pinned version's composition when one was asked for and
              // found; otherwise the appearance's current version. A stale or
              // foreign `?version=` quietly falls back rather than erroring —
              // the identity check already happened on `storeHome.get`.
              sections={pinned ? parseSections(pinned.sections ?? []) : query.data.sections}
              theme={pinned ? parseTheme(pinned.theme) : query.data.theme}
              device={device}
              storeName={query.data.storeName}
              storeSlug={query.data.storeSlug}
              collections={query.data.collections}
              phone={query.data.phone}
              profile={query.data.profile}
            />
          )}
        </main>
      </div>
    </WorkspaceShell>
  );
}
